import { denormalize } from 'normalizr';
import { createSelector } from 'reselect';
import { orgGroup as orgGroupSchema } from '../lib/schema.js';
import { CALL_API, HTTP_METHODS } from '../middlewares/api.js';
import { selectEntities } from './entities.js';
import { selectCurrentOrganizationId } from './auth.js';

// API Actions

const RETRIEVE_ORG_GROUPS_REQUEST =
  'changesync/orgGroups/RETRIEVE_ORG_GROUPS_REQUEST';
const RETRIEVE_ORG_GROUPS_SUCCESS =
  'changesync/orgGroups/RETRIEVE_ORG_GROUPS_SUCCESS';
const RETRIEVE_ORG_GROUPS_FAILURE =
  'changesync/orgGroups/RETRIEVE_ORG_GROUPS_FAILURE';
const UPDATE_ORG_GROUPS_REQUEST =
  'changesync/orgGroups/UPDATE_ORG_GROUPS_REQUEST';
const UPDATE_ORG_GROUPS_SUCCESS =
  'changesync/orgGroups/UPDATE_ORG_GROUPS_SUCCESS';
const UPDATE_ORG_GROUPS_FAILURE =
  'changesync/orgGroups/UPDATE_ORG_GROUPS_FAILURE';
const CREATE_ORG_GROUP_REQUEST =
  'changesync/orgGroups/CREATE_ORG_GROUP_REQUEST';
const CREATE_ORG_GROUP_SUCCESS =
  'changesync/orgGroups/CREATE_ORG_GROUP_SUCCESS';
const CREATE_ORG_GROUP_FAILURE =
  'changesync/orgGroups/CREATE_ORG_GROUP_FAILURE';
const SEARCH_ORG_GROUPS = 'changesync/orgGroups/CREATE_ORG_GROUPS';

const initialState = {
  groupsIds: [],
  pending: false,
  success: false,
  error: null,
};

// Reducer
export default (state = initialState, action) => {
  switch (action.type) {
    case RETRIEVE_ORG_GROUPS_REQUEST:
      return {
        ...state,
        pending: true,
        success: false,
        error: null,
      };
    case RETRIEVE_ORG_GROUPS_SUCCESS:
      return {
        ...state,
        groupsIds: action.payload.result,
        pending: false,
        success: true,
      };
    case RETRIEVE_ORG_GROUPS_FAILURE:
      return {
        ...state,
        groupsIds: action.payload.result,
        pending: false,
        error: action.error,
      };
    default:
      return state;
  }
};

// Action Creators

export const retrieveOrgGroups = (query) => (dispatch, getState) => {
  const organizationId = selectCurrentOrganizationId(getState());
  return dispatch({
    [CALL_API]: {
      types: [
        RETRIEVE_ORG_GROUPS_REQUEST,
        RETRIEVE_ORG_GROUPS_SUCCESS,
        RETRIEVE_ORG_GROUPS_FAILURE,
      ],
      endpoint: `organizations/${organizationId}/groups`,
      method: HTTP_METHODS.GET,
      schema: [orgGroupSchema],
      query,
    },
  });
};

export const searchOrgGroups = (query) => (dispatch, getState) => {
  const organizationId = selectCurrentOrganizationId(getState());
  const { search, ...params } = query;
  return dispatch({
    [CALL_API]: {
      types: [
        RETRIEVE_ORG_GROUPS_REQUEST,
        RETRIEVE_ORG_GROUPS_SUCCESS,
        RETRIEVE_ORG_GROUPS_FAILURE,
      ],
      endpoint: `organizations/${organizationId}/groups`,
      method: HTTP_METHODS.GET,
      schema: [orgGroupSchema],
      query: {
        ...params,
        search,
      },
    },
    meta: {
      debounce: {
        time: 400,
        key: SEARCH_ORG_GROUPS,
      },
    },
  });
};

/**
 * Update groups given a FormData object from the submitted form.
 * FormData is used to include the CSV file or groups tree if groups was added manually.
 *
 * @param organizationId {number}
 * @param formData {FormData}
 * @return {{}} Redux action to be handled by API middleware
 */
export const updateGroups = (organizationId, formData) => ({
  [CALL_API]: {
    types: [
      UPDATE_ORG_GROUPS_REQUEST,
      UPDATE_ORG_GROUPS_SUCCESS,
      UPDATE_ORG_GROUPS_FAILURE,
    ],
    endpoint: `organizations/${organizationId}/organization_structure`,
    method: HTTP_METHODS.PUT,
    body: formData,
  },
});

export const createGroup = (organizationId, group) => ({
  [CALL_API]: {
    types: [
      CREATE_ORG_GROUP_REQUEST,
      CREATE_ORG_GROUP_SUCCESS,
      CREATE_ORG_GROUP_FAILURE,
    ],
    endpoint: `organizations/${organizationId}/groups`,
    method: HTTP_METHODS.POST,
    schema: orgGroupSchema,
    body: { group },
  },
});

// Selectors
export const selectRetrieveGroupsState = (state) => state.orgGroups;

const selectOrgGroupsIds = createSelector(
  selectRetrieveGroupsState,
  (state) => state.groupsIds
);

export const selectOrgGroups = createSelector(
  selectOrgGroupsIds,
  selectEntities,
  (groupsIds, entities) => denormalize(groupsIds, [orgGroupSchema], entities)
);

export const selectFlattenedOrgGroups = createSelector(
  selectOrgGroups,
  (groups) => {
    const flattenedGroups = [];
    const pushGroupAndChildren = (group) => {
      flattenedGroups.push(group);
      group.children.forEach(pushGroupAndChildren);
    };
    groups.forEach(pushGroupAndChildren);
    return flattenedGroups;
  }
);
